"use client";

import Link, { type LinkProps } from "next/link";
import { Text } from "@stone-payments/jade";
import { cn } from "@/utils/classnames";
import { NavigationBackground } from "./navigation-background";
import { NavigationBackgroundImage } from "./navigation-background-image";
import { NavigationLink } from "./navigation-link";
import { NavigationLoading } from "./navigation-loading";
import { NavigationContainer } from "./navigation-container";
import { NavigationLogo } from "./navigation-logo";
import { NavigationActions } from "./navigation-actions";
import { useOrderResumeStore } from "@/stores";
import { OpenStatusTag } from "../open-status-tag";

type NavigationBase = {
  actions?: React.ReactNode[];
  homeLink?: Pick<LinkProps, "href" | "onClick">;
  title: string;
  backgroundUrl?: string;
  logoUrl?: string;
  collapsed?: boolean;
  applyTheme?: boolean | null;
  isOpen?: boolean;
  showOpenTag?: boolean;
};

type NavigationProps =
  | ({ loading: true } & Partial<NavigationBase>)
  | ({ loading?: false } & NavigationBase);

function Navigation({
  actions,
  homeLink = { href: "/" },
  collapsed = false,
  loading,
  title,
  backgroundUrl,
  logoUrl,
  applyTheme = false,
  isOpen = true,
  showOpenTag = false,
}: NavigationProps) {
  const { closeDrawer } = useOrderResumeStore();

  if (loading) {
    return <NavigationLoading collapsed={collapsed} />;
  }

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    homeLink.onClick?.(e);

    closeDrawer();
  };

  return (
    <header
      className={cn("relative bg-white", {
        "border-b border-(--theme-color-border-low)": collapsed,
      })}
    >
      {!collapsed ? (
        <NavigationBackground applyTheme={applyTheme}>
          {backgroundUrl ? (
            <NavigationBackgroundImage
              backgroundUrl={backgroundUrl}
              applyTheme={applyTheme}
            />
          ) : null}
        </NavigationBackground>
      ) : null}

      <NavigationContainer
        className="md:pr-8 md:pl-10 xl:pr-5 xl:pl-8"
        collapsed={collapsed}
      >
        <NavigationLogo
          img={logoUrl}
          alt={`${title} logo`}
          collapsed={collapsed}
          link={{ ...homeLink }}
        />

        <div
          className={cn({
            "mr-8": collapsed,
            "z-10 flex-1 md:order-0": !collapsed,
          })}
        >
          <div
            className={cn("my-0 flex flex-col items-start gap-2 md:my-4", {
              "items-center! md:items-start!": !collapsed,
              "my-4": collapsed,
            })}
          >
            <Text
              weight="semibold"
              className="mt-0! line-clamp-2! text-base! leading-5! tracking-[-0.176px]! wrap-break-word! md:text-lg! md:tracking-[-0.252px]! xl:text-xl! xl:leading-6! xl:tracking-[-0.34px]!"
              data-testid="navigation-title"
            >
              <NavigationLink
                link={{
                  ...homeLink,
                  onClick: (e) => handleLinkClick(e),
                }}
              >
                {title}
              </NavigationLink>
            </Text>

            {showOpenTag ? (
              <OpenStatusTag
                status={isOpen ? "always_open" : "always_closed"}
              />
            ) : null}
          </div>
        </div>

        <NavigationActions collapsed={collapsed}>{actions}</NavigationActions>
      </NavigationContainer>
    </header>
  );
}

export { Navigation, type NavigationProps };
